<template>
  <div class="consumers" v-if="allConsumers.length > 0">

    <h2 class="consumers__title">{{ t('efriends.consumersTitle') }}</h2>

    <div class="consumers__message" v-if="showResidue">
      <span class="consumers__current-price">{{ t('efriends.currentSellingPrice') }}:
        {{ formatPowerPrice(defaultSellingPrice) }}
      </span>
      <router-link v-if="!hasNextDefaultSellingPriceDate" :to="{ name: 'settings' }">
        <IconEdit />
      </router-link>
      <br />
      <span class="consumers__next-price" v-if="hasNextDefaultSellingPriceDate">{{ nextDefaultSellingPriceLabel }}
        {{ formatPowerPrice(nextDefaultSellingPriceFromStore) }}</span>
      <router-link v-if="hasNextDefaultSellingPriceDate" :to="{ name: 'settings' }">
        <IconEdit />
      </router-link>
      <br v-if="hasNextDefaultSellingPriceDate" />

      <template v-if="(!isBusinessPlan && !isFlexPlan) && hasGenerationPlanStandardPrice">
        <template v-if="hasGenerationPlanStandardPrice">
          <span class="consumers__residue">
            {{ t('efriends.residueProduction') }} {{ residueSupplier }}:
            {{ formatPowerPrice(productionResiduePrice) }} </span><br />
          <span v-if="nextProductionResiduePrice !== undefined">
            {{ nextPriceLabel }}
            {{ formatPowerPrice(nextProductionResiduePrice) }}
          </span>
        </template>
      </template>
      <template v-else>
        <span class="consumers__residue">
          {{ t('efriends.residueProduction') }} {{ residueSupplier }} {{ tariffName }}
        </span>
      </template>
    </div>

    <div class="consumers__header" v-if="hasEegConnections">
      <div class="consumers__title">
        <h3>
          {{ t('efriends.communityConnectionsTitle') }}
          <span class="participation__factor">
            ({{ t('efriends.participationFactor') }}: {{ communityParticipationFactor }}%)
          </span>
        </h3>
      </div>

      <!-- <div class="participation__factor">
        <div class="participation__label">
          {{ t('efriends.participationFactorCommunity') }}
        </div>
        <div class="participation__value">
          {{ communityParticipationFactor }}%
        </div>
      </div> -->
    </div>

    <div class="consumers" v-if="consumers.length > 0">
      <div class="consumers__list" data-cy="consumers">
        <transition-group name="consumers__list-item" data-cy="consumers-item" tag="div">
          <div v-for="consumer in consumers" :key="consumer.cubeName" class="consumers__list-item">
            <Consumer :consumer="consumer" />
          </div>
        </transition-group>
      </div>
    </div>
    <div v-else>
      Hier stehen in Zukunft deine verbundenenen Konsumenten.
    </div>
    <br />
  </div>

  <div class="consumers" v-if="hasEegConnections">
    <div class="consumers__header">
      <div class="consumers__title">
        <h3>
          {{ t('efriends.eegConnectionsTitle') }}
          <span class="participation__factor">
            ({{ t('efriends.participationFactor') }}: {{ eegParticipationFactor }}%)
          </span>
        </h3>
      </div>

      <!-- <div class="participation__factor">
        <div class="participation__label">
          {{ t('efriends.participationFactorEEG') }}
        </div>
        <div class="participation__value">
          {{ eegParticipationFactor }}%
        </div>
      </div> -->
    </div>

    <div data-cy="eegConnections" class="consumers__list">
      <transition-group name="consumers__list-item" tag="div">
        <div v-for="cube in eegConnections" :key="cube.cubeName" class="consumers__list-item" data-cy="consumers-item">
          <Consumer :consumer="cube" />
        </div>
      </transition-group>
    </div>
  </div>
</template>

<script setup lang="ts">
  import Consumer from '@/components/efriends/Consumer.vue';
  import IconEdit from '@/components/icons/Edit.vue';
  import { useStrings } from '@/composables/useStrings.ts';
  import { useDate } from '@/composables/useDate.ts';
  import { computed } from 'vue';
  import { useUnits } from '@/composables/useUnits.ts';
  import { useCubeStore } from '@/stores/useCubeStore';
  import { useSettingsStore } from '@/stores/useSettingsStore';
  import { usePlans } from '@/composables/usePlans';
import { NumberUtil } from '@/util/NumberUtil';

  const cubeStore = useCubeStore();
  const settingsStore = useSettingsStore();
  const plans = usePlans();
  const { t } = useStrings();
  const { formatPriceDate } = useDate();
  const { formatPowerPrice } = useUnits();
  const { isFlexPlan: checkIsFlexPlan } = usePlans();

  const gen = computed(() => settingsStore.contractData.plans.gen);

  const cubeDetails = computed(() => cubeStore.cube);
  const communityParticipationFactor = computed(() => NumberUtil.parse(cubeDetails.value?.customerInfo?.communityGenerationParticipationFactor, 100)!);
  const eegParticipationFactor = computed(() => 100 - communityParticipationFactor.value);

  const allConsumers = computed(() => cubeStore.consumers.filter(p => !p.isDonatee));
  const consumers = computed(() => allConsumers.value.filter(p => p.productionType != 'eeg'));
  const eegConnections = computed(() => allConsumers.value.filter(p => p.productionType == 'eeg'));
  const hasEegConnections = computed(() => eegConnections.value?.length > 0);

  const defaultSellingPrice = computed(() => settingsStore.defaultSellingPrice);
  const hasNextDefaultSellingPriceDate = computed(() => settingsStore.nextDefaultSellingPriceDate > new Date());
  const settingsChanged = computed(() => false); // TODO
  const nextDefaultSellingPriceLabel = computed(() => {
    const date = settingsStore.nextDefaultSellingPriceDate;
    if (settingsChanged.value) { return t('efriends.nextPriceLabel'); }
    return `Ab ${date ? formatPriceDate(date) : false}`;
  });
  const nextDefaultSellingPriceFromStore = computed(() => settingsStore.nextDefaultSellingPrice);
  const showResidue = computed(() => cubeStore.supplier.name === 'eFriends Energy GmbH');
  const residueSupplier = computed(() => cubeStore.supplier.name);
  const productionResiduePrice = computed(() => gen.value?.standardPrice);
  const nextProductionResiduePrice = computed(() => gen.value?.nextPrice);
  const nextPriceLabel = computed(() => {
    // TODO What does that do? Why?
    const date = new Date(new Date(gen.value?.nextPriceValidFrom!).getTime() + 2 * 60 * 60 * 1000); //UTC gedöns
    return `Ab ${date ? formatPriceDate(date) : ''}`;
  });

  const isBusinessPlan = computed(() => {
    // TODO APP-574 This looks very much like getters.isBusinessPlan - but it is not.
    const name = gen.value?.name;
    return name !== 'eFRIENDS_CONNECT' &&
      name !== 'eFRIENDS_CONNECT_LieferungvomKunden' &&
      name !== 'eFRIENDS_CONNECT_derbessereTarif_LieferungvomKunden' &&
      name !== 'eFRIENDS_SOFORT' &&
      name !== 'eFRIENDS_SOFORT_LieferungvomKunden' &&
      name !== 'CLEEN Energy CONNECT' &&
      name !== 'CLEEN Energy CONNECT_LieferungvomKunden' &&
      name !== 'eFRIENDS_Osteraktion2019';
  });

  const isFlexPlan = computed(() => {
    return checkIsFlexPlan();
  });

  const hasGenerationPlanStandardPrice = computed(() => {
    return plans.hasGenerationPlanStandardPrice();
  });

  const tariffName = computed(() => {
    return plans.getGenerationPlanName();
  });
</script>

<style lang="scss">
  .consumers {
    margin: $spacer20 auto 0;
    max-width: 800px;

    &__list {
      &-item {
        transition: all 1s ease-in-out 0s;
        margin: 0;

        &-leave-active {
          max-height: 0;
          padding-top: 0;
          padding-bottom: 0;
          border-width: 0;
          overflow: hidden;
        }

        &:last-child {
          border-bottom: none;
        }
      }
    }

    &__message {
      box-shadow: 2px 2px 7px rgba(0, 0, 0, 0.15);
      padding: $spacer10 $spacer30 $spacer10 $spacer20;
      border-left: 4px solid $community;
      background: $white;
      margin: $spacer20 auto;
      max-width: 800px;

      @media (min-width: $bp-ml) {
        flex-basis: 100%;
      }

      .icon {
        width: 1em;
        display: inline-block;
        vertical-align: middle;
        margin-left: 0.2em;
        position: relative;
        top: -0.2em;
      }
    }
  }
</style>
